import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

import { recordThemeChange } from '@/contexts/analytics/themeTracking';

const STORAGE_KEY = 'dnk-mode';

// `system` follows the OS `prefers-color-scheme`.
export type ColorMode = 'light' | 'dark' | 'system';

type ColorModeContextValue = {
  // The user's selected preference — may be `system`. Use this to drive the
  // theme selector UI (which of the three options is active).
  mode: ColorMode;
  // The resolved appearance. When `mode` is `system` this reflects the OS.
  isDark: boolean;
  toggleColorMode: () => void;
  setColorMode: (mode: ColorMode) => void;
};

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  isDark: false,
  toggleColorMode: () => {},
  setColorMode: () => {},
});

const isColorMode = (value: unknown): value is ColorMode =>
  value === 'dark' || value === 'light' || value === 'system';

const getStoredMode = (): ColorMode | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isColorMode(stored) ? stored : null;
  } catch {
    return null;
  }
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

const systemIsDark = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(DARK_QUERY).matches
);

export const ColorModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<ColorMode>(() => getStoredMode() ?? 'light');
  const [osDark, setOsDark] = useState(systemIsDark);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setOsDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const setColorMode = useCallback((newMode: ColorMode) => {
    // Sync the Mixpanel `theme` property/event before persisting, so the change
    // is recorded with the correct previous value (read from localStorage).
    recordThemeChange(newMode);
    setMode(newMode);
    try {
      localStorage.setItem(STORAGE_KEY, newMode);
    } catch {
      // storage unavailable
    }
  }, []);

  const selectedMode: ColorMode = isColorMode(mode) ? mode : 'light';
  const isDark = selectedMode === 'system' ? osDark : selectedMode === 'dark';

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  const toggleColorMode = useCallback(() => {
    setColorMode(isDark ? 'light' : 'dark');
  }, [isDark, setColorMode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({ mode: selectedMode, isDark, toggleColorMode, setColorMode }),
    [selectedMode, isDark, toggleColorMode, setColorMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);
